import { v4 as uuidv4 } from 'uuid';
import { apimCall } from './../helpers/api';
import { showBanner } from './MessageUtility';
import { appContext } from '../AppContext';
import { GoFormzServices } from '../AppSettings';

export const getModalMessage = (title, msg, primaryButtonText = 'Yes', secondaryButtonText = 'No') => ({
  title,
  msg,
  primaryButtonText,
  secondaryButtonText
});

export const formatString = (format, ...args) => {
  let returnValue = format;
  if (!format) {
    throw new Error('Invalid null format string');
  }

  for (let i = 0; i < args.length; i++) {
    if (!args[i]) {
      throw new Error('Invalid null arg parameter');
    }

    returnValue = format.replace(`{${i}}`, args[i]);
  }

  if (returnValue.match(/{-?\d+}/g)) {
    throw new Error('Index (zero based) must be greater than or equal to zero and less than the size of the argument list');
  }

  return returnValue;
};

// https://stackoverflow.com/questions/7837456/how-to-compare-arrays-in-javascript
export const compareArrays = (array1: any[], array2: any[]): boolean => {
  // if either array is falsy, return
  if (!array1 || !array2) {
    return false;
  }

  // compare lengths - can save a lot of time
  if (array1.length !== array2.length) {
    return false;
  }

  for (let i = 0, l = array1.length; i < l; i++) {
    // Check if we have nested arrays
    if (array1[i] instanceof Array && array2[i] instanceof Array) {
      // recurse into the nested arrays
      if (!compareArrays(array1[i] as any[], array2[i] as any[])) {
        return false;
      }
    } else if (array1[i] !== array2[i]) {
      // Warning - two different object instances will never be equal: {x:20} != {x:20}
      return false;
    }
  }
  return true;
};

export const compareObjects = (a, b) => {
  for (const prop in a) {
    if (a.hasOwnProperty(prop)) {
      if (a[prop] != b[prop]) {
        return true;
      }
    }
  }
  for (const prop in b) {
    if (b.hasOwnProperty(prop)) {
      if (a[prop] != b[prop]) {
        return true;
      }
    }
  }
  return false;
};

export function debounce(func, delay) {
  let timeoutId;

  return function (...args) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function () {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    if (!lastRan) {
      func.apply(this, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

export const tryWhen = (whenFunc, func?, previousRetryCount?) => {
  if (whenFunc()) {
    func();
  } else {
    const retryCount = previousRetryCount ? previousRetryCount + 1 : 0;
    if (retryCount < 100) {
      setTimeout(() => tryWhen(whenFunc, func, retryCount), 10);
    }
  }
};

export const handleWebResponseErrors = (response) => {
  // https://www.tjvantoll.com/2015/09/13/fetch-and-errors/
  if (!response.ok) {
    throw Error(response.statusText);
  }
  return response;
};

const guids = [];
export async function getGuidsFromServer(count: number) {
  try {
    const response = await apimCall(GoFormzServices.ApimService, `generateids/${count}?${uuidv4()}`);
    Array.prototype.push.apply(guids, response.data);
  } catch (err) {
    console.warn('Failed to get IDs from server');
    for (let i = 0; i < count; i++) {
      guids.push(uuidv4());
    }
  }
}

export const getGuid = (): string => {
  const guid = guids.pop();

  if (guids.length < 100) {
    getGuidsFromServer(10000);
  }

  return guid;
};

export const getGuidAsync = async (preloadQuantity = 10): Promise<string> => {
  if (guids.length == 0) {
    await getGuidsFromServer(preloadQuantity);
  }

  const guid = guids.pop();

  return guid;
};

export function isGuid(value) {
  const pattern = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
  return pattern.test(value);
}

export const copyTextToClipboard = async (text: string, bannerMessage?: string) => {
  let textArea: HTMLTextAreaElement;

  const showBannerMsg = (success: boolean) => {
    if (bannerMessage && success) {
      showBanner(bannerMessage);
    }
  };

  try {
    // see if the clipboard api is available to use
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      showBannerMsg(true);

      return true;
    }

    // deprecated approach
    textArea = document.createElement('textarea');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();

    const successful = document.execCommand('copy');

    showBannerMsg(successful);

    return successful;
  } catch (err) {
    console.error(err.message);
    return false;
  } finally {
    if (textArea) {
      document.body.removeChild(textArea);
    }
  }
};

export const getPathInfo = (path) => {
  const hash = path[0] === '#' ? path.slice(1) : path;
  const qIdx = hash.indexOf('?') === -1 ? hash.length : hash.indexOf('?');
  const params = hash
    .substring(qIdx + 1)
    .split('&')
    .reduce((prev, curr) => {
      const copy = Object.assign({}, prev);
      copy[curr.split('=')[0]] = curr.split('=')[1];
      return copy;
    }, {});
  const pathname = `/${hash.substring(0, qIdx)}`.replace('//', '/');
  return {
    pathname,
    params
  };
};

export const getQuerystringFromParams = (params) => {
  let result = '';
  const paramArray = [];
  if (params) {
    for (const param in params) {
      // eslint-disable-next-line no-prototype-builtins
      if (params.hasOwnProperty(param)) {
        paramArray.push(`${param}=${params[param]}`);
      }
    }
    result = `?${paramArray.join('&')}`;
  }
  return result;
};

export const getPathInfoFromLocation = (ignoreHash = false) => {
  let path = `${location.pathname}${location.search}`;
  if (window.location.hash && !ignoreHash) {
    path = `${window.location.hash.substr(1)}`;
  }
  return getPathInfo(path);
};

/** @deprecated This is legacy. Use [getSearchParams](./urlUtility.ts) instead. */
export const getQuerystringParameters = (useLowerCase?: boolean, ignoreHash = false) => {
  const params = getPathInfoFromLocation(ignoreHash).params;
  if (useLowerCase) {
    const result = {};
    for (const param in params) {
      // eslint-disable-next-line no-prototype-builtins
      if (params.hasOwnProperty(param)) {
        result[param.toLowerCase()] = params[param];
      }
    }
    return result;
  }
  return params;
};

export function selectAllTextInElement(element: HTMLElement) {
  if (window.getSelection && document.createRange) {
    const range = document.createRange();
    range.selectNodeContents(element);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

export const placeCaretAtEnd = (el) => {
  // http://stackoverflow.com/questions/4233265/contenteditable-set-caret-at-the-end-of-the-text-cross-browser
  el.focus();
  el.scrollLeft = el.scrollWidth;
  if (typeof window.getSelection !== 'undefined' && typeof document.createRange !== 'undefined') {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } else if (typeof (document.body as any).createTextRange !== 'undefined') {
    const textRange = (document.body as any).createTextRange();
    textRange.moveToElementText(el);
    textRange.collapse(false);
    textRange.select();
  }
};

export function setCaretPosition(elem, caretPos) {
  if (elem.createTextRange) {
    const range = elem.createTextRange();
    range.move('character', caretPos);
    range.select();
  } else if (elem.selectionStart) {
    elem.focus();
    elem.setSelectionRange(caretPos, caretPos);
  } else elem.focus();
}

export function getTextWidth(text: string, font: string) {
  // re-use canvas object for better performance
  const canvas = (getTextWidth as any).canvas || ((getTextWidth as any).canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
}

export const throwIfParameterMissing = (parameter) => {
  throw new Error(`Missing parameter: ${parameter}`);
};

export const waitUntil = (until: () => boolean) =>
  new Promise<void>(async (callback) => {
    const tryCallback = function () {
      if (until()) {
        callback();
      } else {
        setTimeout(tryCallback, 5);
      }
    };
    tryCallback();
  });

export const loadScript = (src: string, loadedCheck: () => boolean, async = false) =>
  new Promise<void>(async (callback) => {
    if (loadedCheck()) {
      callback();
    } else {
      const head = document.getElementsByTagName('head')[0];
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = src;
      if (async) {
        script.async = true;
      }
      head.appendChild(script);
      const tryCallback = function () {
        if (loadedCheck()) {
          callback();
        } else {
          setTimeout(tryCallback, 5);
        }
      };
      tryCallback();
    }
  });

export const getFormattedCurrency = (currency, minFractionDigits = 0) => {
  const fraction = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: minFractionDigits
  });

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  });

  if (currency % 1 == 0 && minFractionDigits === 0) {
    // handle values with a single decimal, so 25.5 should be 25.50
    return fraction.format(currency);
  } else {
    return formatter.format(currency);
  }
};

export function getLocale() {
  const state = appContext.store.getState();
  const locale = state.profile.client.locale;

  if (locale && locale !== '') {
    return locale;
  }
  return 'en-US';
}

export function deepClone<T>(obj: T): T {
  if (!obj) {
    return null;
  }
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export const isValidEmail = (email: string) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const isEventKeyDown = (event) => {
  if (!event) {
    return false;
  }

  return event.key === 'Enter' || event.key === ' ';
};
